/**
 * @vuelto-surface
 * mobile: ok
 * desktop: n/a
 * strategy: inherit
 */
import React from 'react';
import { formatArPhoneDisplay } from '@shared/phone';
import { TicketListRow } from '../../shared/ticket/TicketListRow';
import { getDunningLevel, getOverdueDays } from '../application/customerDunning';
import { hasCustomerCreditLimit } from '../application/customerCreditLimit';
import type { CustomerAccountRow } from '../domain/customerTypes';
import { formatMoney } from '../../shared/format/public-api';

type AppointmentPreview = {
  lastVisitAt: string | null;
  nextAppointmentAt: string | null;
};

type Props = {
  item: CustomerAccountRow;
  onDetail: (row: CustomerAccountRow) => void;
  canViewDebt?: boolean;
  appointmentPreview?: AppointmentPreview | null;
};

function customerStatus(item: CustomerAccountRow): {
  label: string;
  tone: 'ok' | 'bad' | 'warn' | 'muted';
} | null {
  if (item.blocked) return { label: 'Bloqueado', tone: 'bad' };
  const dunning = getDunningLevel(item);
  if (dunning !== 'none') {
    const days = getOverdueDays(item) ?? 0;
    return { label: `Vencido ${days}d`, tone: dunning === 'r1' ? 'warn' : 'bad' };
  }
  if (item.balance < 0) return { label: 'A favor', tone: 'ok' };
  if (item.balance === 0) return null;
  if (item.creditLimit > 0) {
    const utilization = item.balance / item.creditLimit;
    if (utilization >= 1) return { label: 'Sobre el límite', tone: 'bad' };
    if (utilization >= 0.8) return { label: 'Casi al límite', tone: 'warn' };
  }
  return { label: 'Deuda', tone: 'warn' };
}

function formatPreviewDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
}

function buildMetaText(item: CustomerAccountRow, preview?: AppointmentPreview | null): string {
  const parts: string[] = [];
  if (item.phone) parts.push(formatArPhoneDisplay(item.phone));
  if (item.cuit) parts.push(`CUIT ${item.cuit}`);
  else if (hasCustomerCreditLimit(item.creditLimit)) parts.push(`Límite ${formatMoney(item.creditLimit)}`);
  if (preview) {
    const apptParts: string[] = [];
    if (preview.lastVisitAt) apptParts.push(`Última ${formatPreviewDate(preview.lastVisitAt)}`);
    if (preview.nextAppointmentAt) apptParts.push(`Próximo ${formatPreviewDate(preview.nextAppointmentAt)}`);
    if (apptParts.length > 0) parts.push(apptParts.join(' · '));
  }
  return parts.join(' · ');
}

function CustomerCardInner({ item, onDetail, canViewDebt = true, appointmentPreview }: Props) {
  const balanceText = canViewDebt ? formatMoney(item.balance) : '•••';
  const status = customerStatus(item);
  const metaText = buildMetaText(item, appointmentPreview);
  const initials = item.name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');

  return (
    <TicketListRow
      title={item.name}
      meta={metaText || null}
      value={balanceText}
      statusLabel={status?.label ?? null}
      statusTone={status?.tone}
      inactive={item.blocked}
      avatar={initials ? { initials, tone: item.blocked ? 'bad' : 'gold' } : null}
      onPress={() => onDetail(item)}
      accessibilityLabel={`${item.name}, saldo ${balanceText}`}
    />
  );
}

export const CustomerCard = React.memo(CustomerCardInner);
